import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { NavParams } from "ionic-angular";
import { Subscription } from "rxjs";
import { commonVar } from "../../../../providers/common-var/common";
import { CompanyDetails } from "../company-details";
import { npiValidator } from "../../../../utils/npi";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";

@Component({
  selector: "company-add-location",
  templateUrl: "company-add-location.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CompanyAddLocation),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CompanyAddLocation),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyAddLocation
  extends CompanyDetails
  implements ControlValueAccessor, OnDestroy, OnInit
{
  subscriptions: Subscription[] = [];
  stateList = commonVar.STATE_LIST;
  countryList = commonVar.COUNTRIES;
  primaryList = ["Hospice", "Palliative"];

  phoneMask = commonVar.PHONE_US_MASK;
  timezoneList = commonVar.LOCATION_TIMEZONE_LIST;
  federalTaxIDMask = "00-000000";

  @Input() isEditMode: boolean = false;
  // @Input() isFacility: boolean = true;

  // CO-3393 (A.2.5–A.2.7): optional Medi-Cal Provider ID shown next to the
  // NPI, flag-gated. Reference data only — never read by claim generation.
  californiaMediCalSupportToggle: boolean = false;

  set value(value: any) {
    if (value.phone)
      value.phone = value.phone.replace(
        /^(\d{3})(\d{3})(\d{4}).*/,
        "($1)$2-$3"
      );
    if (value.fax)
      value.fax = value.fax.replace(/^(\d{3})(\d{3})(\d{4}).*/, "($1)$2-$3");
    this.innerFormGroup.patchValue(value);
  }

  get primaryType() {
    return this.innerFormGroup.get("primaryType");
  }

  constructor(
    protected formBuilder: FormBuilder,
    protected navParams: NavParams,
    private stateManagementService: StateManagementService,
    private cdRef: ChangeDetectorRef
  ) {
    super(formBuilder);
    // create inner form
    this.innerFormGroup = this.formBuilder.group({
      city: ["", Validators.required],
      contact: [""],
      displayCode: [""],
      email: [""],
      facilityType: [true], // Required
      fax: ["", [Validators.minLength(13)]],
      federalTaxId: ["", Validators.minLength(9)],
      // An NPI is 10 digits (9 significant + a Luhn check digit). This used to be
      // minLength(9) against a maxlength="9" input, so a real NPI could not even be typed
      // and the 9-digit stub was then declared valid and saved. npiValidator accepts an
      // empty value, accepts 9 (we derive the check digit on save), and REJECTS a 10-digit
      // value whose check digit is wrong — that is a typo, not an NPI.
      hospiceNpi: ["", npiValidator],
      hospiceTaxonomy: [
        "",
        [Validators.minLength(10), Validators.pattern(".{9}X{1}")],
      ],
      locationGroup: [""], // Required
      medicaidProviderId: [""],
      name: ["", Validators.required],
      palliativeNpi: ["", npiValidator],
      palliativeTaxonomy: [
        "",
        [Validators.minLength(10), Validators.pattern(".{9}X{1}")],
      ],
      phone: ["", [Validators.required, Validators.minLength(13)]],
      postal: [
        "",
        [
          Validators.required,
          Validators.minLength(5),
          Validators.pattern("\\d{5}"),
        ],
      ],
      postalExtension: [
        "",
        [
          Validators.required,
          Validators.minLength(4),
          Validators.pattern("\\d{4}"),
        ],
      ],
      primaryType: ["", Validators.required], // TODO - name this to something more descriptive? hospice vs palliative
      state: [null, Validators.required],
      address: ["", Validators.required],
      address2: [""],
      country: [null, Validators.required],
      website: [""], // TODO - update website on backend to not be required
      timezone: [""],
      tenantId: [
        0,
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
    });

    this.subscriptions.push(
      this.innerFormGroup.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );

    this.subscriptions.push(
      this.innerFormGroup.get("primaryType").valueChanges.subscribe((value) => {
        this.updateValidator(value);
      })
    );
  }

  ngOnInit() {
    // this.showLocationType = !this.isEditMode;
    this.subscriptions.push(
      this.stateManagementService.californiaMediCalSupportToggle.subscribe(
        (enabled) => {
          this.californiaMediCalSupportToggle = !!enabled;
          this.cdRef.markForCheck();
        }
      )
    );
  }

  valid(control: string) {
    return this.innerFormGroup.get(control).valid;
  }

  touched(control: string) {
    return this.innerFormGroup.get(control).touched;
  }

  validate(_: FormControl) {
    return this.innerFormGroup.valid
      ? null
      : { locationGroup: { valid: false }, facility: { valid: false } };
  }

  updateValidator(primaryType: string) {
    if (primaryType === "Hospice") {
      this.innerFormGroup
        .get("hospiceNpi")
        .setValidators([npiValidator, Validators.required]);
      // commented in users branch
      this.innerFormGroup
        .get("hospiceTaxonomy")
        .setValidators([
          Validators.required,
          Validators.minLength(10),
          Validators.pattern(".{9}X{1}"),
        ]);
      this.innerFormGroup
        .get("palliativeNpi")
        .setValidators([npiValidator]);
      this.innerFormGroup
        .get("palliativeTaxonomy")
        .setValidators([
          Validators.minLength(10),
          Validators.pattern(".{9}X{1}"),
        ]);
    } else if (primaryType === "Palliative") {
      this.innerFormGroup
        .get("palliativeNpi")
        .setValidators([npiValidator, Validators.required]);
      // commented in users branch
      this.innerFormGroup
        .get("palliativeTaxonomy")
        .setValidators([
          Validators.required,
          Validators.minLength(10),
          Validators.pattern(".{9}X{1}"),
        ]);
      this.innerFormGroup
        .get("hospiceNpi")
        .setValidators([npiValidator]);
      this.innerFormGroup
        .get("hospiceTaxonomy")
        .setValidators([
          Validators.minLength(10),
          Validators.pattern(".{9}X{1}"),
        ]);
    }
    this.innerFormGroup.get("palliativeNpi").updateValueAndValidity();
    this.innerFormGroup.get("palliativeTaxonomy").updateValueAndValidity();
    this.innerFormGroup.get("hospiceNpi").updateValueAndValidity();
    this.innerFormGroup.get("hospiceTaxonomy").updateValueAndValidity();
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/company-details/add-location/company-add-location.ts